import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

// ─── Particle Canvas ──────────────────────────────────────────────────────────
function ParticleCanvas() {
  const canvasRef = useRef(null);
  const raf = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let W = window.innerWidth;
    let H = window.innerHeight;

    // Canvas covers the viewport only — we use clientY (no scroll offset)
    const resize = () => {
      W = canvas.width = window.innerWidth;
      H = canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const COLORS = ['#818cf8', '#a78bfa', '#38bdf8', '#34d399', '#f472b6', '#fb923c'];

    // Floating glowing rings — spread across viewport
    const RINGS = Array.from({ length: 8 }, (_, i) => ({
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      r: 70 + Math.random() * 150,
      vx: (Math.random() - 0.5) * 0.3,
      vy: (Math.random() - 0.5) * 0.3,
      color: COLORS[i % COLORS.length],
      phase: Math.random() * Math.PI * 2,
    }));

    // Particles — home positions spread across viewport
    const COUNT = Math.min(Math.floor(window.innerWidth / 6), 180);
    const PARTS = Array.from({ length: COUNT }, () => {
      const x = Math.random() * window.innerWidth;
      const y = Math.random() * window.innerHeight;
      return {
        x, y, ox: x, oy: y,
        vx: 0, vy: 0,
        r: Math.random() * 2.5 + 1,
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        alpha: 0.65 + Math.random() * 0.35,
        mass: 0.5 + Math.random() * 1.0,
      };
    });

    // Mouse uses clientX/clientY — no scroll offset since canvas is fixed viewport
    const mouse = { x: -9999, y: -9999, active: false };
    const onMove = (e) => {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
      mouse.active = true;
    };
    const onLeave = () => { mouse.active = false; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseleave', onLeave);

    let t = 0;
    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      t += 0.01;

      // ── Floating rings ──────────────────────────────────────────────────
      RINGS.forEach(ring => {
        ring.x += ring.vx;
        ring.y += ring.vy;
        if (ring.x < -ring.r * 2) ring.x = W + ring.r;
        if (ring.x > W + ring.r * 2) ring.x = -ring.r;
        if (ring.y < -ring.r * 2) ring.y = H + ring.r;
        if (ring.y > H + ring.r * 2) ring.y = -ring.r;

        const pr = ring.r + Math.sin(t * 0.8 + ring.phase) * 20;

        // Outer glow ring
        ctx.save();
        ctx.globalAlpha = 0.30;
        ctx.shadowColor = ring.color;
        ctx.shadowBlur = 24;
        ctx.beginPath();
        ctx.arc(ring.x, ring.y, pr, 0, Math.PI * 2);
        ctx.strokeStyle = ring.color;
        ctx.lineWidth = 2;
        ctx.stroke();
        ctx.restore();

        // Inner ring
        ctx.save();
        ctx.globalAlpha = 0.12;
        ctx.shadowColor = ring.color;
        ctx.shadowBlur = 10;
        ctx.beginPath();
        ctx.arc(ring.x, ring.y, pr * 0.52, 0, Math.PI * 2);
        ctx.strokeStyle = ring.color;
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.restore();

        // Radial fill glow
        const grd = ctx.createRadialGradient(ring.x, ring.y, pr * 0.3, ring.x, ring.y, pr);
        grd.addColorStop(0, ring.color + '00');
        grd.addColorStop(0.55, ring.color + '1a');
        grd.addColorStop(1, ring.color + '00');
        ctx.beginPath();
        ctx.arc(ring.x, ring.y, pr, 0, Math.PI * 2);
        ctx.fillStyle = grd;
        ctx.fill();
      });

      // ── Cursor glow + ring ──────────────────────────────────────────────
      if (mouse.active) {
        const cg = ctx.createRadialGradient(mouse.x, mouse.y, 0, mouse.x, mouse.y, 120);
        cg.addColorStop(0, 'rgba(129,140,248,0.28)');
        cg.addColorStop(0.45, 'rgba(167,139,250,0.10)');
        cg.addColorStop(1, 'rgba(129,140,248,0)');
        ctx.beginPath();
        ctx.arc(mouse.x, mouse.y, 120, 0, Math.PI * 2);
        ctx.fillStyle = cg;
        ctx.fill();

        ctx.save();
        ctx.globalAlpha = 0.65;
        ctx.shadowColor = '#818cf8';
        ctx.shadowBlur = 16;
        ctx.beginPath();
        ctx.arc(mouse.x, mouse.y, 22, 0, Math.PI * 2);
        ctx.strokeStyle = '#818cf8';
        ctx.lineWidth = 1.5;
        ctx.stroke();
        ctx.restore();
      }

      // ── Antigravity particles ───────────────────────────────────────────
      PARTS.forEach(p => {
        const dx = p.x - mouse.x;
        const dy = p.y - mouse.y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (mouse.active && dist < 160 && dist > 0) {
          const force = ((160 - dist) / 160) * 4.5 / p.mass;
          p.vx += (dx / dist) * force;
          p.vy += (dy / dist) * force;
        }

        // Spring back to home
        p.vx += (p.ox - p.x) * 0.04;
        p.vy += (p.oy - p.y) * 0.04;

        // Damping
        p.vx *= 0.86;
        p.vy *= 0.86;

        p.x += p.vx;
        p.y += p.vy;

        const tw = 0.75 + 0.25 * Math.sin(t * 3 + p.ox * 0.05);
        ctx.save();
        ctx.globalAlpha = p.alpha * tw;
        ctx.shadowColor = p.color;
        ctx.shadowBlur = 12;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.fill();
        ctx.restore();
      });

      // ── Connection lines ────────────────────────────────────────────────
      for (let i = 0; i < PARTS.length; i++) {
        for (let j = i + 1; j < PARTS.length; j++) {
          const dx = PARTS[i].x - PARTS[j].x;
          const dy = PARTS[i].y - PARTS[j].y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < 90) {
            ctx.save();
            ctx.globalAlpha = 0.15 * (1 - d / 90);
            ctx.beginPath();
            ctx.moveTo(PARTS[i].x, PARTS[i].y);
            ctx.lineTo(PARTS[j].x, PARTS[j].y);
            ctx.strokeStyle = '#818cf8';
            ctx.lineWidth = 0.7;
            ctx.stroke();
            ctx.restore();
          }
        }
      }

      raf.current = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(raf.current);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseleave', onLeave);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed', top: 0, left: 0,
        width: '100vw', height: '100vh',
        zIndex: 0, pointerEvents: 'none',
      }}
    />
  );
}

// ─── Animated Counter ─────────────────────────────────────────────────────────
function Counter({ target, suffix = '', duration = 2000 }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        let start = 0;
        const step = target / (duration / 16);
        const t = setInterval(() => {
          start += step;
          if (start >= target) { setVal(target); clearInterval(t); }
          else setVal(Math.floor(start));
        }, 16);
        obs.disconnect();
      }
    }, { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [target, duration]);
  return <span ref={ref}>{val.toLocaleString()}{suffix}</span>;
}

// ─── Stat Card ────────────────────────────────────────────────────────────────
function StatCard({ label, value, suffix, color, delay }) {
  return (
    <div style={{ ...sc.statCard, animationDelay: delay, borderTop: `3px solid ${color}` }}>
      <div style={{ ...sc.statIcon, background: color + '22' }}>
        <div style={{ width: 12, height: 12, borderRadius: '50%', background: color, boxShadow: `0 0 12px ${color}, 0 0 24px ${color}55` }} />
      </div>
      <div style={sc.statVal}><Counter target={value} suffix={suffix} /></div>
      <div style={sc.statLabel}>{label}</div>
    </div>
  );
}

// ─── Feature Card ─────────────────────────────────────────────────────────────
function FeatureCard({ title, desc, color, delay }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      style={{ ...sc.featCard, ...(hov ? { transform: 'translateY(-8px)', boxShadow: `0 20px 40px rgba(0,0,0,0.4), 0 0 0 1px ${color}44` } : {}), animationDelay: delay }}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
    >
      <div style={{ ...sc.featIcon, background: `linear-gradient(135deg, ${color}33, ${color}11)`, border: `1px solid ${color}44`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: 14, height: 14, borderRadius: '50%', background: color, boxShadow: `0 0 14px ${color}, 0 0 28px ${color}55` }} />
      </div>
      <h3 style={sc.featTitle}>{title}</h3>
      <p style={sc.featDesc}>{desc}</p>
    </div>
  );
}

// ─── Portal Card ──────────────────────────────────────────────────────────────
function PortalCard({ icon, title, desc, route, gradient, delay, navigate }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      style={{ ...sc.portalCard, ...(hov ? { transform: 'translateY(-6px) scale(1.02)', boxShadow: '0 24px 48px rgba(0,0,0,0.5)' } : {}), animationDelay: delay }}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      onClick={() => navigate(route)}
    >
      <div style={{ ...sc.portalTop, background: gradient }}>
        <span style={sc.portalEmoji}>{icon}</span>
      </div>
      <div style={sc.portalBody}>
        <h3 style={sc.portalTitle}>{title}</h3>
        <p style={sc.portalDesc}>{desc}</p>
        <div style={{ ...sc.portalBtn, background: gradient }}>Enter Portal →</div>
      </div>
    </div>
  );
}

// ─── Navbar ───────────────────────────────────────────────────────────────────
function Navbar({ navigate }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [regOpen, setRegOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const loginRef = useRef(null);
  const regRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (loginRef.current && !loginRef.current.contains(e.target)) setMenuOpen(false);
      if (regRef.current && !regRef.current.contains(e.target)) setRegOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const loginOptions = [
    { label: '🎓 Student Login',   route: '/login/student' },
    { label: '👨‍🏫 Faculty Login',   route: '/login/faculty' },
    { label: '🛡️ Admin Login',     route: '/admin-login' },
    { label: '⚖️ Authority Login', route: '/authority-login' },
  ];
  const registerOptions = [
    { label: '🎓 Student Register', route: '/register/student' },
    { label: '👨‍🏫 Faculty Register', route: '/register/faculty' },
  ];

  const dropdownStyle = {
    position: 'absolute', top: 'calc(100% + 8px)', right: 0,
    background: 'rgba(20,18,50,0.98)', backdropFilter: 'blur(20px)',
    border: '1px solid rgba(255,255,255,0.12)', borderRadius: 12,
    overflow: 'hidden', minWidth: 200,
    boxShadow: '0 16px 40px rgba(0,0,0,0.5)', zIndex: 200,
    animation: 'navSlideDown 0.2s ease-out',
  };

  return (
    <nav style={sc.navbar}>
      <div style={sc.navInner}>
        {/* Brand */}
        <div style={sc.navBrand} onClick={() => navigate('/')}>
          <span style={sc.navLogo}>🎓</span>
          <span style={sc.navName}>UniResolve</span>
        </div>

        {/* Desktop links */}
        <div className="hp-desktop-nav" style={sc.navLinks}>
          {/* Register dropdown */}
          <div ref={regRef} style={{ position: 'relative' }}>
            <button style={{ ...sc.navLoginBtn, background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)' }}
              onClick={() => { setRegOpen(o => !o); setMenuOpen(false); }}>
              Register <span style={{ fontSize: 10, marginLeft: 4 }}>{regOpen ? '▲' : '▼'}</span>
            </button>
            {regOpen && (
              <div style={dropdownStyle}>
                {registerOptions.map(opt => (
                  <div key={opt.route} style={sc.dropdownItem} className="ur-dropdown-item"
                    onClick={() => { setRegOpen(false); navigate(opt.route); }}>
                    {opt.label}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Login dropdown */}
          <div ref={loginRef} style={{ position: 'relative' }}>
            <button style={sc.navLoginBtn} onClick={() => { setMenuOpen(o => !o); setRegOpen(false); }}>
              Login <span style={{ fontSize: 10, marginLeft: 4 }}>{menuOpen ? '▲' : '▼'}</span>
            </button>
            {menuOpen && (
              <div style={dropdownStyle}>
                {loginOptions.map(opt => (
                  <div key={opt.route} style={sc.dropdownItem} className="ur-dropdown-item"
                    onClick={() => { setMenuOpen(false); navigate(opt.route); }}>
                    {opt.label}
                  </div>
                ))}
              </div>
            )}
          </div>


        </div>

        {/* Mobile right */}
        <div className="hp-mobile-nav" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>

          <button onClick={() => setMobileOpen(o => !o)} style={{
            width: 36, height: 36, borderRadius: 9,
            background: mobileOpen ? 'rgba(99,102,241,0.25)' : 'rgba(255,255,255,0.1)',
            border: `1px solid ${mobileOpen ? 'rgba(99,102,241,0.5)' : 'rgba(255,255,255,0.2)'}`,
            color: '#fff', fontSize: 18, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {mobileOpen ? '✕' : '☰'}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div style={{
          background: 'rgba(15,12,41,0.98)', backdropFilter: 'blur(20px)',
          borderTop: '1px solid rgba(255,255,255,0.08)',
          padding: '12px 16px 20px',
        }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: 1, padding: '8px 4px 4px' }}>Register</div>
          {registerOptions.map(o => (
            <div key={o.route} style={{ padding: '11px 8px', color: '#e2e8f0', fontSize: 14, cursor: 'pointer', borderRadius: 8 }}
              onClick={() => { setMobileOpen(false); navigate(o.route); }}>
              {o.label}
            </div>
          ))}
          <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: 1, padding: '12px 4px 4px' }}>Login</div>
          {loginOptions.map(o => (
            <div key={o.route} style={{ padding: '11px 8px', color: '#e2e8f0', fontSize: 14, cursor: 'pointer', borderRadius: 8 }}
              onClick={() => { setMobileOpen(false); navigate(o.route); }}>
              {o.label}
            </div>
          ))}
        </div>
      )}

      <style>{`
        @keyframes navSlideDown { from { opacity:0; transform:translateY(-6px); } to { opacity:1; transform:translateY(0); } }
        .ur-dropdown-item:hover { background: linear-gradient(135deg,#3b82f6,#8b5cf6) !important; color:#fff !important; }
        @media (min-width: 769px) { .hp-mobile-nav { display: none !important; } }
        @media (max-width: 768px) { .hp-desktop-nav { display: none !important; } }
      `}</style>
    </nav>
  );
}

// ─── Main HomePage ────────────────────────────────────────────────────────────
function HomePage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total_complaints: 0, resolved_complaints: 0, pending_complaints: 0, verified_complaints: 0, average_resolution_hours: 0 });
  const [recentComplaints, setRecentComplaints] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    injectStyles();
    Promise.all([
      api.get('/dashboard/statistics').catch(() => ({ data: null })),
      api.get('/dashboard/recent').catch(() => ({ data: [] })),
    ]).then(([s, r]) => {
      if (s.data) setStats(s.data);
      if (Array.isArray(r.data)) setRecentComplaints(r.data.slice(0, 6));
      setLoaded(true);
    });
  }, []);

  const resolutionRate = stats.total_complaints > 0
    ? Math.round((stats.resolved_complaints / stats.total_complaints) * 100) : 0;
  const avgDays = stats.average_resolution_hours > 0
    ? (stats.average_resolution_hours / 24).toFixed(1) : '—';

  const statusColor = { pending: '#f59e0b', verified: '#6366f1', resolved: '#10b981', rejected: '#ef4444', escalated: '#f97316' };
  const statusBg = { pending: '#f59e0b22', verified: '#6366f122', resolved: '#10b98122', rejected: '#ef444422', escalated: '#f9731622' };

  return (
    <div style={sc.page}>
      <ParticleCanvas />
      <Navbar navigate={navigate} />

      {/* ── HERO ── */}
      <section style={sc.hero}>
        <div style={sc.heroOrb1} /><div style={sc.heroOrb2} />
        <div style={sc.heroContent}>
          <div style={sc.heroLogoRow}>
            {/* Animated icon */}
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <div style={{
                position: 'absolute', inset: -6, borderRadius: 26,
                background: 'linear-gradient(135deg,#6366f1,#8b5cf6,#06b6d4)',
                opacity: 0.4, filter: 'blur(12px)',
                animation: 'orbFloat 3s ease-in-out infinite',
              }} />
              <div style={sc.heroLogoIcon}>🎓</div>
            </div>
            {/* Name + tagline stacked */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={sc.heroLogoName}>UniResolve</span>
              <span style={sc.heroLogoTag}>University Complaint Management System</span>
            </div>
          </div>
          <h1 style={sc.heroTitle}>
            Resolve Issues.<br />
            <span style={sc.heroAccent}>Faster. Smarter.</span>
          </h1>
          <div style={{ margin: '0 0 36px', maxWidth: 640 }}>
            {/* Main quote line */}
            <p style={{
              fontSize: 'clamp(15px, 1.8vw, 19px)',
              fontWeight: 300,
              fontStyle: 'italic',
              letterSpacing: '0.2px',
              lineHeight: 1.75,
              color: 'rgba(255,255,255,0.55)',
              margin: '0 0 20px',
              fontFamily: 'Georgia, "Times New Roman", serif',
            }}>
              &ldquo;From the Moment a Complaint is Raised to the Second It&rsquo;s Resolved&rdquo;
            </p>

            {/* Three highlighted phrases */}
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0 }}>
              {[
                { text: 'Every Step is Visible', color: '#818cf8' },
                { text: 'Every Action is Tracked', color: '#a78bfa' },
                { text: 'Every Voice is Heard', color: '#22d3ee' },
              ].map((item, i) => (
                <span key={i} style={{ display: 'inline-flex', alignItems: 'center' }}>
                  {i > 0 && (
                    <span style={{
                      display: 'inline-block', width: 4, height: 4, borderRadius: '50%',
                      background: 'rgba(255,255,255,0.2)', margin: '0 14px', flexShrink: 0,
                    }} />
                  )}
                  <span style={{
                    fontSize: 'clamp(12px, 1.3vw, 14px)',
                    fontWeight: 700,
                    letterSpacing: '1.2px',
                    textTransform: 'uppercase',
                    color: item.color,
                    textShadow: `0 0 20px ${item.color}66`,
                  }}>{item.text}</span>
                </span>
              ))}
            </div>

            {/* Underline accent */}
            <div style={{
              marginTop: 16, height: 1,
              background: 'linear-gradient(90deg, #6366f1, #a78bfa 50%, transparent)',
              borderRadius: 1,
            }} />
          </div>
          <div style={sc.heroBtns}>
            <button style={sc.heroBtnPrimary} onClick={() => navigate('/login/student')}>
              Get Started →
            </button>
            <button style={sc.heroBtnSecondary} onClick={() => navigate('/login/faculty')}>
              Faculty Portal
            </button>
          </div>
          <div style={sc.heroTrust}>
            <span style={sc.trustDot} />
            <span style={sc.trustText}>Live system · Real-time updates · Secure</span>
          </div>
        </div>

      </section>

      {/* ── LIVE STATS ── */}
      <section style={sc.section}>
        <div style={sc.sectionInner}>
          <div style={sc.sectionLabel}>Real-Time Statistics</div>
          <h2 style={sc.sectionTitle}>System at a Glance</h2>
          <p style={sc.sectionSub}>Live numbers pulled directly from the database</p>
          <div style={sc.statsGrid}>
            <StatCard label="Total Complaints" value={stats.total_complaints} color="#6366f1" delay="0s" />
            <StatCard label="Resolved" value={stats.resolved_complaints} color="#10b981" delay="0.1s" />
            <StatCard label="Pending" value={stats.pending_complaints} color="#f59e0b" delay="0.2s" />
            <StatCard label="Verified" value={stats.verified_complaints} color="#8b5cf6" delay="0.3s" />
            <StatCard label="Resolution Rate" value={resolutionRate} suffix="%" color="#06b6d4" delay="0.4s" />
            <StatCard label="Avg Resolution" value={parseFloat(avgDays) || 0} suffix=" days" color="#f97316" delay="0.5s" />
          </div>
        </div>
      </section>

      {/* ── PORTALS ── */}
      <section style={{ ...sc.section, background: 'rgba(99,102,241,0.04)' }}>
        <div style={sc.sectionInner}>
          <div style={sc.sectionLabel}>Access Portals</div>
          <h2 style={sc.sectionTitle}>Choose Your Portal</h2>
          <p style={sc.sectionSub}>Each role has a dedicated, secure access point</p>
          <div style={sc.portalsGrid}>
            <PortalCard icon="🎓" title="Student Portal" desc="Submit complaints, track status, upvote issues, and get notified on resolution." route="/login/student" gradient="linear-gradient(135deg,#6366f1,#8b5cf6)" delay="0s" navigate={navigate} />
            <PortalCard icon="👨‍🏫" title="Faculty Portal" desc="Raise academic and infrastructure concerns on behalf of your department." route="/login/faculty" gradient="linear-gradient(135deg,#06b6d4,#3b82f6)" delay="0.1s" navigate={navigate} />
            <PortalCard icon="👨‍💼" title="Admin Portal" desc="Review, verify, and assign complaints. Manage your department's resolution pipeline." route="/admin-login" gradient="linear-gradient(135deg,#f59e0b,#ef4444)" delay="0.2s" navigate={navigate} />
            <PortalCard icon="👑" title="Authority Portal" desc="Full oversight — monitor all departments, manage admins, and generate reports." route="/authority-login" gradient="linear-gradient(135deg,#10b981,#059669)" delay="0.3s" navigate={navigate} />
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section style={sc.section}>
        <div style={sc.sectionInner}>
          <div style={sc.sectionLabel}>Process</div>
          <h2 style={sc.sectionTitle}>How It Works</h2>
          <p style={sc.sectionSub}>From submission to resolution in 4 clear steps</p>
          <div style={sc.stepsRow}>
            {[
              { n: '01', color: '#6366f1', title: 'Submit', desc: 'Student or faculty submits a complaint with details, category, and optional attachments.' },
              { n: '02', color: '#06b6d4', title: 'Verify', desc: 'Department admin reviews and verifies the complaint, then assigns it for resolution.' },
              { n: '03', color: '#10b981', title: 'Resolve', desc: 'Assigned authority works on the issue and updates the status with resolution notes.' },
              { n: '04', color: '#f59e0b', title: 'Feedback', desc: 'Complainant rates the resolution. Data feeds into department performance metrics.' },
            ].map((step, i) => (
              <div key={i} style={sc.step}>
                <div style={sc.stepNum}>{step.n}</div>
                <div style={{ ...sc.stepIcon, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ width: 16, height: 16, borderRadius: '50%', background: step.color, boxShadow: `0 0 16px ${step.color}, 0 0 32px ${step.color}55` }} />
                </div>
                <h3 style={sc.stepTitle}>{step.title}</h3>
                <p style={sc.stepDesc}>{step.desc}</p>
                {i < 3 && <div style={sc.stepArrow}>→</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section style={{ ...sc.section, background: 'rgba(99,102,241,0.04)' }}>
        <div style={sc.sectionInner}>
          <div style={sc.sectionLabel}>Features</div>
          <h2 style={sc.sectionTitle}>Everything You Need</h2>
          <p style={sc.sectionSub}>Built for universities that take student welfare seriously</p>
          <div style={sc.featGrid}>
            <FeatureCard title="Real-Time Notifications" desc="Instant alerts via email and in-app notifications at every status change." color="#6366f1" delay="0s" />
            <FeatureCard title="File Attachments" desc="Attach images and documents as evidence to support your complaint." color="#8b5cf6" delay="0.05s" />
            <FeatureCard title="Community Upvoting" desc="Upvote complaints to signal urgency. High-upvote issues get prioritized." color="#06b6d4" delay="0.1s" />
            <FeatureCard title="Deadline Tracking" desc="Every complaint has a resolution deadline. Overdue ones auto-escalate." color="#f59e0b" delay="0.15s" />
            <FeatureCard title="Analytics Dashboard" desc="Admins and authority get full analytics — trends, performance, resolution rates." color="#10b981" delay="0.2s" />
            <FeatureCard title="Role-Based Access" desc="4 distinct roles with isolated portals. No data leakage between roles." color="#ef4444" delay="0.25s" />
            <FeatureCard title="Auto Escalation" desc="Unresolved complaints past deadline automatically escalate to authority." color="#f97316" delay="0.3s" />
            <FeatureCard title="Feedback & Ratings" desc="Rate resolutions to keep admins accountable and improve service quality." color="#eab308" delay="0.35s" />
          </div>
        </div>
      </section>

      {/* ── RECENT COMPLAINTS ── */}
      {recentComplaints.length > 0 && (
        <section style={sc.section}>
          <div style={sc.sectionInner}>
            <div style={sc.sectionLabel}>Live Feed</div>
            <h2 style={sc.sectionTitle}>Recent Activity</h2>
            <p style={sc.sectionSub}>Latest verified and resolved complaints from the system</p>
            <div style={sc.complaintsGrid}>
              {recentComplaints.map((c, i) => (
                <div key={c.id} style={{ ...sc.complaintCard, animationDelay: `${i * 0.07}s` }}>
                  <div style={sc.ccTop}>
                    <span style={{ ...sc.ccStatus, background: statusBg[c.status] || '#6366f122', color: statusColor[c.status] || '#6366f1' }}>
                      {c.status}
                    </span>
                    <span style={sc.ccDept}>{c.department}</span>
                  </div>
                  <h4 style={sc.ccTitle}>{c.title}</h4>
                  <p style={sc.ccDesc}>{c.description}</p>
                  <div style={sc.ccFoot}>
                    <span style={sc.ccUpvotes}>👍 {c.upvotes || 0}</span>
                    <span style={sc.ccTime}>{c.created_at ? new Date(c.created_at).toLocaleDateString() : ''}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── CTA ── */}
      <section style={sc.cta}>
        <div style={sc.ctaOrb1} /><div style={sc.ctaOrb2} />
        <div style={sc.ctaInner}>
          <h2 style={sc.ctaTitle}>Ready to Make Your Voice Heard?</h2>
          <p style={sc.ctaSub}>Join thousands of students and faculty using UniResolve to drive real change on campus.</p>
          <div style={sc.ctaBtns}>
            <button style={sc.ctaBtnPrimary} onClick={() => navigate('/register/student')}>Register as Student</button>
            <button style={sc.ctaBtnSecondary} onClick={() => navigate('/register/faculty')}>Register as Faculty</button>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer style={sc.footer}>
        <div style={sc.footerInner}>
          <div style={sc.footerBrand}>
            <span style={sc.footerLogo}>🎓</span>
            <span style={sc.footerName}>UniResolve</span>
          </div>
          <p style={sc.footerTagline}>Empowering universities with transparent complaint management.</p>
          <div style={sc.footerLinks}>
            <span style={sc.footerLink} onClick={() => navigate('/login/student')}>Student Login</span>
            <span style={sc.footerDot}>·</span>
            <span style={sc.footerLink} onClick={() => navigate('/login/faculty')}>Faculty Login</span>
            <span style={sc.footerDot}>·</span>
            <span style={sc.footerLink} onClick={() => navigate('/admin-login')}>Admin</span>
            <span style={sc.footerDot}>·</span>
            <span style={sc.footerLink} onClick={() => navigate('/authority-login')}>Authority</span>
          </div>
          <p style={sc.footerCopy}>© 2026 UniResolve. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const BG = 'linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)';
const CARD = 'linear-gradient(145deg, rgba(255,255,255,0.09) 0%, rgba(255,255,255,0.04) 100%)';
const BORDER = '1px solid rgba(255,255,255,0.08)';
const FONT = "'Segoe UI', system-ui, sans-serif";

const sc = {
  page: { background: BG, minHeight: '100vh', fontFamily: FONT, color: '#fff', position: 'relative' },

  // Hero
  hero: { minHeight: '92vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '140px 24px 80px', position: 'relative', flexDirection: 'column', textAlign: 'center' },
  heroOrb1: { position: 'absolute', width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(circle, rgba(99,102,241,0.18) 0%, transparent 70%)', top: -100, right: -100, pointerEvents: 'none' },
  heroOrb2: { position: 'absolute', width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(139,92,246,0.15) 0%, transparent 70%)', bottom: 0, left: -80, pointerEvents: 'none' },
  heroContent: { maxWidth: 720, position: 'relative', zIndex: 2, animation: 'fadeUp 0.8s ease-out', display: 'flex', flexDirection: 'column', alignItems: 'center' },
  heroLogoRow: {
    display: 'flex', alignItems: 'center', gap: 18, marginBottom: 36,
    animation: 'fadeUp 0.7s ease-out both',
  },
  heroLogoIcon: {
    width: 68, height: 68, borderRadius: 20,
    background: 'linear-gradient(135deg,#4f46e5,#7c3aed,#0891b2)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 32, flexShrink: 0, position: 'relative',
    boxShadow: '0 8px 32px rgba(99,102,241,0.5)',
    animation: 'logoPop 0.7s cubic-bezier(0.34,1.56,0.64,1) both, logoGlow 3s ease-in-out 0.7s infinite',
    border: '1px solid rgba(255,255,255,0.15)',
  },
  heroLogoName: {
    fontSize: 42, fontWeight: 900, letterSpacing: '-1.5px', lineHeight: 1,
    background: 'linear-gradient(135deg, #e0e7ff 0%, #a5b4fc 45%, #67e8f9 100%)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
    animation: 'nameSlide 0.6s ease-out 0.15s both',
  },
  heroLogoTag: {
    fontSize: 13, fontWeight: 500, letterSpacing: '0.5px',
    color: 'rgba(165,180,252,0.7)',
    animation: 'nameSlide 0.6s ease-out 0.3s both',
  },
  heroBadge: { display: 'none' },
  heroTitle: { fontSize: 'clamp(42px,6vw,72px)', fontWeight: 900, lineHeight: 1.1, margin: '0 0 20px', letterSpacing: '-1.5px' },
  heroAccent: { background: 'linear-gradient(135deg,#6366f1,#a78bfa,#06b6d4)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' },
  heroSub: { fontSize: 18, color: 'rgba(255,255,255,0.6)', lineHeight: 1.7, margin: '0 0 36px', maxWidth: 560 },
  heroBtns: { display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 28, justifyContent: 'center' },
  heroBtnPrimary: { padding: '14px 36px', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: '#fff', border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 700, cursor: 'pointer', boxShadow: '0 8px 24px rgba(99,102,241,0.4)', transition: 'transform 0.2s' },
  heroBtnSecondary: { padding: '14px 36px', background: 'rgba(255,255,255,0.08)', color: '#fff', border: BORDER, borderRadius: 12, fontSize: 16, fontWeight: 600, cursor: 'pointer', transition: 'background 0.2s' },
  heroTrust: { display: 'flex', alignItems: 'center', gap: 8 },
  trustDot: { width: 8, height: 8, borderRadius: '50%', background: '#10b981', boxShadow: '0 0 8px #10b981', animation: 'pulse 2s infinite' },
  trustText: { fontSize: 13, color: 'rgba(255,255,255,0.45)' },
};

Object.assign(sc, {
  // Sections
  section: { padding: '80px 24px', position: 'relative', zIndex: 1 },
  sectionInner: { maxWidth: 1100, margin: '0 auto' },
  sectionLabel: { display: 'inline-block', padding: '5px 14px', background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.3)', borderRadius: 20, fontSize: 12, color: '#a5b4fc', marginBottom: 14, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 },
  sectionTitle: { fontSize: 'clamp(26px,4vw,42px)', fontWeight: 800, margin: '0 0 12px', letterSpacing: '-0.5px' },
  sectionSub: { fontSize: 16, color: 'rgba(255,255,255,0.5)', margin: '0 0 48px', maxWidth: 520 },

  // Stats
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 20 },
  statCard: { background: CARD, backdropFilter: 'blur(16px)', border: BORDER, borderRadius: 16, padding: '24px 20px', textAlign: 'center', animation: 'fadeUp 0.6s ease-out both', transition: 'transform 0.2s' },
  statIcon: { width: 48, height: 48, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, margin: '0 auto 12px' },
  statVal: { fontSize: 32, fontWeight: 800, color: '#fff', marginBottom: 6, letterSpacing: '-0.5px' },
  statLabel: { fontSize: 13, color: 'rgba(255,255,255,0.5)', fontWeight: 500 },

  // Portals
  portalsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 20 },
  portalCard: { background: CARD, backdropFilter: 'blur(16px)', border: BORDER, borderRadius: 20, overflow: 'hidden', cursor: 'pointer', transition: 'transform 0.25s, box-shadow 0.25s', animation: 'fadeUp 0.6s ease-out both', boxShadow: '0 8px 24px rgba(0,0,0,0.3)' },
  portalTop: { padding: '28px 24px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  portalEmoji: { fontSize: 48 },
  portalBody: { padding: '20px 24px 24px' },
  portalTitle: { fontSize: 18, fontWeight: 700, margin: '0 0 8px', color: '#fff' },
  portalDesc: { fontSize: 13, color: 'rgba(255,255,255,0.55)', lineHeight: 1.6, margin: '0 0 16px' },
  portalBtn: { display: 'inline-block', padding: '8px 18px', borderRadius: 8, fontSize: 13, fontWeight: 700, color: '#fff', cursor: 'pointer' },

  // Steps
  stepsRow: { display: 'flex', gap: 0, flexWrap: 'wrap', position: 'relative' },
  step: { flex: '1 1 200px', background: CARD, backdropFilter: 'blur(16px)', border: BORDER, borderRadius: 16, padding: '28px 24px', position: 'relative', margin: '0 8px 16px' },
  stepNum: { fontSize: 11, fontWeight: 800, color: 'rgba(99,102,241,0.6)', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 12 },
  stepIcon: { fontSize: 32, marginBottom: 12 },
  stepTitle: { fontSize: 18, fontWeight: 700, margin: '0 0 8px', color: '#fff' },
  stepDesc: { fontSize: 13, color: 'rgba(255,255,255,0.5)', lineHeight: 1.6, margin: 0 },
  stepArrow: { position: 'absolute', right: -20, top: '50%', transform: 'translateY(-50%)', fontSize: 20, color: 'rgba(99,102,241,0.5)', zIndex: 2 },

  // Features
  featGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 18 },
  featCard: { background: CARD, backdropFilter: 'blur(16px)', border: BORDER, borderRadius: 16, padding: '24px', transition: 'transform 0.25s, box-shadow 0.25s', animation: 'fadeUp 0.6s ease-out both', cursor: 'default' },
  featIcon: { width: 48, height: 48, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, marginBottom: 14 },
  featTitle: { fontSize: 16, fontWeight: 700, margin: '0 0 8px', color: '#fff' },
  featDesc: { fontSize: 13, color: 'rgba(255,255,255,0.5)', lineHeight: 1.6, margin: 0 },

  // Recent complaints
  complaintsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 18 },
  complaintCard: { background: CARD, backdropFilter: 'blur(16px)', border: BORDER, borderRadius: 16, padding: '20px', animation: 'fadeUp 0.6s ease-out both' },
  ccTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 },
  ccStatus: { padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 },
  ccDept: { fontSize: 11, color: 'rgba(255,255,255,0.4)', fontWeight: 500 },
  ccTitle: { fontSize: 14, fontWeight: 700, color: '#fff', margin: '0 0 6px', lineHeight: 1.4 },
  ccDesc: { fontSize: 12, color: 'rgba(255,255,255,0.45)', lineHeight: 1.5, margin: '0 0 12px' },
  ccFoot: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  ccUpvotes: { fontSize: 12, color: '#a5b4fc', fontWeight: 600 },
  ccTime: { fontSize: 11, color: 'rgba(255,255,255,0.3)' },

  // CTA
  cta: { padding: '100px 24px', position: 'relative', overflow: 'hidden', zIndex: 1, textAlign: 'center' },
  ctaOrb1: { position: 'absolute', width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(99,102,241,0.2) 0%, transparent 70%)', top: -100, left: '50%', transform: 'translateX(-50%)', pointerEvents: 'none' },
  ctaOrb2: { position: 'absolute', width: 300, height: 300, borderRadius: '50%', background: 'radial-gradient(circle, rgba(139,92,246,0.15) 0%, transparent 70%)', bottom: -50, right: '10%', pointerEvents: 'none' },
  ctaInner: { position: 'relative', zIndex: 2, maxWidth: 600, margin: '0 auto' },
  ctaTitle: { fontSize: 'clamp(28px,4vw,46px)', fontWeight: 900, margin: '0 0 16px', letterSpacing: '-0.5px' },
  ctaSub: { fontSize: 17, color: 'rgba(255,255,255,0.55)', margin: '0 0 36px', lineHeight: 1.6 },
  ctaBtns: { display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' },
  ctaBtnPrimary: { padding: '14px 32px', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: '#fff', border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 700, cursor: 'pointer', boxShadow: '0 8px 24px rgba(99,102,241,0.4)' },
  ctaBtnSecondary: { padding: '14px 32px', background: 'rgba(255,255,255,0.08)', color: '#fff', border: BORDER, borderRadius: 12, fontSize: 16, fontWeight: 600, cursor: 'pointer' },

  // Navbar
  navbar: { position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100, background: 'rgba(15,12,41,0.85)', backdropFilter: 'blur(16px)', borderBottom: '1px solid rgba(255,255,255,0.07)' },
  navInner: { maxWidth: 1100, margin: '0 auto', padding: '0 24px', height: 60, display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  navBrand: { display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' },
  navLogo: { fontSize: 22 },
  navName: { fontSize: 18, fontWeight: 800, color: '#fff', letterSpacing: '-0.3px' },
  navLinks: { display: 'flex', alignItems: 'center', gap: 16 },
  navLink: { fontSize: 14, color: 'rgba(255,255,255,0.65)', cursor: 'pointer', fontWeight: 500, padding: '4px 8px', borderRadius: 6, transition: 'color 0.2s' },
  navLoginBtn: { padding: '8px 20px', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: '#fff', border: 'none', borderRadius: 9, fontSize: 14, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center' },
  loginDropdown: { position: 'absolute', top: 'calc(100% + 8px)', right: 0, background: 'rgba(20,18,50,0.98)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 12, overflow: 'hidden', minWidth: 200, boxShadow: '0 16px 40px rgba(0,0,0,0.5)', zIndex: 200 },
  dropdownItem: { padding: '12px 18px', fontSize: 14, color: 'rgba(255,255,255,0.85)', cursor: 'pointer', fontWeight: 500, transition: 'background 0.15s', borderBottom: '1px solid rgba(255,255,255,0.06)' },

  // Footer
  footer: { borderTop: BORDER, padding: '40px 24px', position: 'relative', zIndex: 1 },
  footerInner: { maxWidth: 1100, margin: '0 auto', textAlign: 'center' },
  footerBrand: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 10 },
  footerLogo: { fontSize: 28 },
  footerName: { fontSize: 22, fontWeight: 800, color: '#fff' },
  footerTagline: { fontSize: 14, color: 'rgba(255,255,255,0.4)', margin: '0 0 20px' },
  footerLinks: { display: 'flex', justifyContent: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 20 },
  footerLink: { fontSize: 13, color: 'rgba(255,255,255,0.5)', cursor: 'pointer', padding: '4px 8px', borderRadius: 6, transition: 'color 0.2s' },
  footerDot: { color: 'rgba(255,255,255,0.2)', lineHeight: '28px' },
  footerCopy: { fontSize: 12, color: 'rgba(255,255,255,0.25)', margin: 0 },
});

// ─── Keyframes injection ──────────────────────────────────────────────────────
function injectStyles() {
  const id = 'ur-home-styles';
  if (document.getElementById(id)) return;
  const s = document.createElement('style');
  s.id = id;
  s.textContent = `
    @keyframes fadeUp { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
    @keyframes pulse { 0%,100%{box-shadow:0 0 0 0 rgba(16,185,129,0.5)} 50%{box-shadow:0 0 0 8px rgba(16,185,129,0)} }
    @keyframes logoGlow { 0%,100%{box-shadow:0 0 24px rgba(99,102,241,0.5),0 0 48px rgba(139,92,246,0.2)} 50%{box-shadow:0 0 40px rgba(99,102,241,0.8),0 0 80px rgba(139,92,246,0.4)} }
    @keyframes logoPop { 0%{opacity:0;transform:scale(0.5) rotate(-15deg)} 70%{transform:scale(1.12) rotate(4deg)} 100%{opacity:1;transform:scale(1) rotate(0deg)} }
    @keyframes nameSlide { 0%{opacity:0;transform:translateX(-20px)} 100%{opacity:1;transform:translateX(0)} }
    @keyframes shimmer { 0%{background-position:200% center} 100%{background-position:-200% center} }
    @keyframes orbFloat { 0%,100%{transform:translateY(0) scale(1)} 50%{transform:translateY(-8px) scale(1.04)} }
    * { box-sizing: border-box; }
    ::-webkit-scrollbar { width: 6px; }
    ::-webkit-scrollbar-track { background: #0f0c29; }
    ::-webkit-scrollbar-thumb { background: rgba(99,102,241,0.4); border-radius: 3px; }
    .ur-dropdown-item:hover { background: rgba(99,102,241,0.15) !important; color: #fff !important; }
  `;
  document.head.appendChild(s);
}

export default HomePage;
